import React, { useState, useEffect, useRef, useCallback } from 'react';

// One step per unlocked stop, ending at the goal. A strong team reaches about 14 stops in 3 hours.
const LABELS = [...Array.from({ length: 13 }, (_, i) => String(i + 1)), '🐟'];
const STOPS = LABELS.map((label, i) => ({ label, position: 8 + (84 * i) / (LABELS.length - 1) }));

export const TIMELINE_LENGTH = STOPS.length;

const DURATION = 3500;

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function buildPhases(level) {
  const startX = level === 0 ? 0 : STOPS[level - 1].position;
  const endX = STOPS[level].position;
  return [
    { start: 0,    end: 0.55, fromX: startX, toX: endX, stopIdx: -1, moving: true },
    { start: 0.55, end: 1.00, fromX: endX,   toX: endX, stopIdx: level, moving: false },
  ];
}

export default function TimelineAnimation({ onComplete, level = 0 }) {
  const clampedLevel = Math.min(Math.max(level, 0), STOPS.length - 1);
  const phasesRef = useRef(buildPhases(clampedLevel));
  const startX = clampedLevel === 0 ? 0 : STOPS[clampedLevel - 1].position;

  const [walkerX, setWalkerX] = useState(startX);
  const [highestReached, setHighestReached] = useState(clampedLevel - 1);
  const [isWalking, setIsWalking] = useState(true);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    startRef.current = performance.now();
    const phases = phasesRef.current;

    function tick(now) {
      const elapsed = now - startRef.current;
      const t = Math.min(elapsed / DURATION, 1);

      let phase = phases[phases.length - 1];
      for (const p of phases) {
        if (t < p.end) { phase = p; break; }
      }

      const pt = Math.min((t - phase.start) / (phase.end - phase.start), 1);
      const eased = phase.moving ? easeInOutCubic(pt) : pt;
      const x = phase.fromX + (phase.toX - phase.fromX) * eased;

      setWalkerX(x);
      setIsWalking(phase.moving);
      if (phase.stopIdx >= 0) {
        setHighestReached(prev => Math.max(prev, phase.stopIdx));
      }

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        onCompleteRef.current();
      }
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const skip = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    onCompleteRef.current();
  }, []);

  return (
    <div className="tl-anim-overlay" onClick={skip}>
      <div className="tl-anim-scene">
        <div className="tl-anim-track">
          <div className="tl-anim-red-line" />

          {STOPS.map((s, i) => (
            <div
              key={i}
              className={`tl-anim-stop${highestReached >= i ? ' reached' : ''}${i % 2 === 1 ? ' above' : ''}`}
              style={{ left: `${s.position}%` }}
            >
              <div className="tl-anim-dot" />
              <span className="tl-anim-label">{s.label}</span>
            </div>
          ))}

          <div
            className={`tl-anim-walker${isWalking ? ' walking' : ''}`}
            style={{ left: `${walkerX}%` }}
          >
            <svg viewBox="0 0 40 64" width="32" height="52">
              <circle cx="20" cy="8" r="7" fill="#fff" />
              <line x1="20" y1="15" x2="20" y2="38" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
              <g className="tl-arm-left">
                <line x1="20" y1="22" x2="10" y2="32" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </g>
              <g className="tl-arm-right">
                <line x1="20" y1="22" x2="30" y2="32" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </g>
              <g className="tl-leg-left">
                <line x1="20" y1="38" x2="12" y2="55" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </g>
              <g className="tl-leg-right">
                <line x1="20" y1="38" x2="28" y2="55" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </g>
            </svg>
          </div>
        </div>

        <p className="tl-anim-skip">Trykk for å hoppe over</p>
      </div>
    </div>
  );
}
